import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { WithBoundArgs } from '@glint/template';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import LineClamp from 'garaje/components/line-clamp';
import type GroupInvite from 'garaje/models/group-invite';

interface NameCellSignature {
  Args: {
    row: GroupInvite;
    isLoading?: boolean;
  };
  Element: HTMLDivElement;
}

export function createCurriedNameCell(isLoading?: boolean): WithBoundArgs<typeof NameCell, 'isLoading'> {
  const CurriedNameCell: TOC<{ Args: { row: GroupInvite }; Element: HTMLDivElement }> = <template>
    <NameCell @row={{@row}} @isLoading={{isLoading}} ...attributes />
  </template>;

  return CurriedNameCell as WithBoundArgs<typeof NameCell, 'isLoading'>;
}

export default class NameCell extends Component<NameCellSignature> {
  delay = 400;
  lineHeight = 1.25;
  lines = 2;
  popperContainer = '.ui__page';
  tooltipClass = 'text-sm font-bold break-word max-w-sm z-50';

  @tracked textWasTruncated = false;

  setTextWasTruncated = (value: boolean): void => {
    this.textWasTruncated = value;
  };

  <template>
    <div class="w-[16rem]" ...attributes data-test-group-invites-list-name-cell>
      {{#if @isLoading}}
        <div class="skeleton-loading-boxes">
          <div class="text w-48 h-4 rounded-md mb-2">&nbsp;</div>
        </div>
      {{else}}
        <LinkTo @route="visitors.invites.groups.show" @model={{@row.id}} class="text-gem-50 hover:underline">
          <LineClamp
            @handleTruncate={{this.setTextWasTruncated}}
            @lineHeight={{this.lineHeight}}
            @lines={{this.lines}}
            @text={{@row.groupName}}
            aria-hidden="true"
            data-test-group-invites-list-name-cell-content
          />

          <div class="sr-only">
            {{! Full group name available to screen reader }}
            <span data-test-group-invites-list-name-cell-content>{{@row.groupName}}</span>
          </div>

          {{#if this.textWasTruncated}}
            <EmberPopover
              @delay={{this.delay}}
              @popperContainer={{this.popperContainer}}
              @text={{@row.groupName}}
              @tooltipClass={{this.tooltipClass}}
              data-test-group-invites-list-name-cell-tooltip
            />
          {{/if}}
        </LinkTo>
      {{/if}}
    </div>
  </template>
}
